import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardContent } from '../components/ui/Card';
import { GraduationCap, Building2, School, ArrowRight, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function SignupPage() {
  const [step, setStep] = useState<'type' | 'details' | 'verification'>('type');
  const [userType, setUserType] = useState<'student' | 'landlord' | 'university'>('student');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    university: '',
    studentId: '',
    propertyCount: '',
    department: ''
  });
  const navigate = useNavigate();
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStep('verification');
    // Simulate verification
    setTimeout(() => {
      navigate(`/dashboard/${userType}`);
    }, 2000);
  };
  const benefits = {
    student: ['Access to verified hostels only', 'AI-powered roommate matching', 'Secure escrow payments', '24/7 safety monitoring'],
    landlord: ['Reach verified students', 'Automated rent collection', 'Property verification badge', 'Tenant screening tools'],
    university: ['Track student locations', 'Safety monitoring dashboard', 'Ambassador management', 'Compliance reporting']
  };
  return <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8" data-id="element-1033">
      <div className="sm:mx-auto sm:w-full sm:max-w-md" data-id="element-1034">
        <Link to="/" className="flex justify-center items-center gap-2 mb-6" data-id="element-1035">
          <img src="https://cdn.magicpatterns.com/uploads/37CjFFhCaYFTTZ1oinSNtZ/Cam_main_ver_logo.png" alt="CampusAparte" className="h-12 w-auto" data-id="element-1036" />
        </Link>
        <h2 className="text-center text-3xl font-bold tracking-tight text-brand-dark" data-id="element-1037">
          Create your account
        </h2>
        <p className="mt-2 text-center text-sm text-slate-600" data-id="element-1038">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-brand-primary hover:text-brand-light" data-id="element-1039">
            Sign in
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-2xl" data-id="element-1040">
        <AnimatePresence mode="wait" data-id="element-1041">
          {step === 'type' && <motion.div key="type" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} data-id="element-1042">
              <Card data-id="element-1043">
                <CardContent className="p-8" data-id="element-1044">
                  <h3 className="text-xl font-bold text-brand-dark mb-6 text-center" data-id="element-1045">
                    Choose your account type
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-id="element-1046">
                    {[{
                  id: 'student',
                  icon: GraduationCap,
                  label: 'Student/Parent',
                  desc: 'Find verified housing'
                }, {
                  id: 'landlord',
                  icon: Building2,
                  label: 'Landlord/Agent',
                  desc: 'List your property'
                }, {
                  id: 'university',
                  icon: School,
                  label: 'University Admin',
                  desc: 'Monitor students'
                }].map(type => <button key={type.id} onClick={() => setUserType(type.id as any)} className={`p-6 rounded-xl border-2 text-center transition-all ${userType === type.id ? 'border-brand-primary bg-brand-primary/5 ring-2 ring-brand-primary/20' : 'border-slate-200 hover:border-brand-primary/50 hover:bg-slate-50'}`} data-id="element-1047">
                        <type.icon className={`w-8 h-8 mx-auto mb-3 ${userType === type.id ? 'text-brand-primary' : 'text-slate-400'}`} data-id="element-1048" />
                        <div className="font-bold text-brand-dark mb-1" data-id="element-1049">
                          {type.label}
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-1050">
                          {type.desc}
                        </div>
                      </button>)}
                  </div>

                  <div className="bg-slate-50 rounded-xl p-6 mb-6" data-id="element-1051">
                    <h4 className="font-bold text-brand-dark mb-3" data-id="element-1052">
                      What you'll get:
                    </h4>
                    <ul className="space-y-2" data-id="element-1053">
                      {benefits[userType].map((benefit, idx) => <li key={idx} className="flex items-center gap-2 text-sm text-slate-700" data-id="element-1054">
                          <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" data-id="element-1055" />
                          {benefit}
                        </li>)}
                    </ul>
                  </div>

                  <Button className="w-full" size="lg" onClick={() => setStep('details')} data-id="element-1056">
                    Continue as{' '}
                    {userType.charAt(0).toUpperCase() + userType.slice(1)}
                    <ArrowRight className="ml-2 w-5 h-5" data-id="element-1057" />
                  </Button>
                </CardContent>
              </Card>
            </motion.div>}

          {step === 'details' && <motion.div key="details" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} data-id="element-1058">
              <Card data-id="element-1059">
                <CardContent className="p-8" data-id="element-1060">
                  <div className="flex items-center justify-between mb-6" data-id="element-1061">
                    <h3 className="text-xl font-bold text-brand-dark" data-id="element-1062">
                      Your details
                    </h3>
                    <button onClick={() => setStep('type')} className="text-sm text-slate-500 hover:text-brand-primary" data-id="element-1063">
                      ← Back
                    </button>
                  </div>

                  <form className="space-y-4" onSubmit={handleSubmit} data-id="element-1064">
                    <div className="grid grid-cols-2 gap-4" data-id="element-1065">
                      <Input label="First Name" value={formData.firstName} onChange={e => setFormData({
                    ...formData,
                    firstName: e.target.value
                  })} required data-id="element-1066" />
                      <Input label="Last Name" value={formData.lastName} onChange={e => setFormData({
                    ...formData,
                    lastName: e.target.value
                  })} required data-id="element-1067" />
                    </div>

                    <Input label="Email Address" type="email" value={formData.email} onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} placeholder={userType === 'university' ? 'admin@university.edu.ng' : 'you@example.com'} required data-id="element-1068" />

                    <Input label="Phone Number" type="tel" value={formData.phone} onChange={e => setFormData({
                  ...formData,
                  phone: e.target.value
                })} placeholder="+234 800 000 0000" required data-id="element-1069" />

                    {userType === 'student' && <>
                        <Input label="University" value={formData.university} onChange={e => setFormData({
                    ...formData,
                    university: e.target.value
                  })} placeholder="e.g. University of Lagos" required data-id="element-1070" />
                        <Input label="Student ID (Optional)" value={formData.studentId} onChange={e => setFormData({
                    ...formData,
                    studentId: e.target.value
                  })} placeholder="Your student ID number" data-id="element-1071" />
                      </>}

                    {userType === 'landlord' && <Input label="Number of Properties" type="number" value={formData.propertyCount} onChange={e => setFormData({
                  ...formData,
                  propertyCount: e.target.value
                })} placeholder="How many properties do you manage?" required data-id="element-1072" />}

                    {userType === 'university' && <>
                        <Input label="University Name" value={formData.university} onChange={e => setFormData({
                    ...formData,
                    university: e.target.value
                  })} placeholder="e.g. University of Lagos" required data-id="element-1073" />
                        <Input label="Department" value={formData.department} onChange={e => setFormData({
                    ...formData,
                    department: e.target.value
                  })} placeholder="e.g. Student Affairs" required data-id="element-1074" />
                      </>}

                    <Input label="Password" type="password" value={formData.password} onChange={e => setFormData({
                  ...formData,
                  password: e.target.value
                })} placeholder="Min. 8 characters" required data-id="element-1075" />

                    <Input label="Confirm Password" type="password" value={formData.confirmPassword} onChange={e => setFormData({
                  ...formData,
                  confirmPassword: e.target.value
                })} placeholder="Re-enter your password" required data-id="element-1076" />

                    <div className="pt-4" data-id="element-1077">
                      <label className="flex items-start gap-2 text-sm text-slate-600 mb-4" data-id="element-1078">
                        <input type="checkbox" required className="mt-0.5 rounded border-slate-300 text-brand-primary focus:ring-brand-primary" data-id="element-1079" />
                        <span data-id="element-1080">
                          I agree to the{' '}
                          <a href="#" className="text-brand-primary hover:underline" data-id="element-1081">
                            Terms of Service
                          </a>{' '}
                          and{' '}
                          <a href="#" className="text-brand-primary hover:underline" data-id="element-1082">
                            Privacy Policy
                          </a>
                        </span>
                      </label>

                      <Button type="submit" className="w-full" size="lg" data-id="element-1083">
                        Create Account
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </motion.div>}

          {step === 'verification' && <motion.div key="verification" initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} data-id="element-1084">
              <Card data-id="element-1085">
                <CardContent className="p-12 text-center" data-id="element-1086">
                  <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                delay: 0.2,
                type: 'spring'
              }} className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1087">
                    <Check className="w-10 h-10 text-emerald-600" data-id="element-1088" />
                  </motion.div>
                  <h3 className="text-2xl font-bold text-brand-dark mb-2" data-id="element-1089">
                    Account Created!
                  </h3>
                  <p className="text-slate-600 mb-6" data-id="element-1090">
                    We've sent a verification email to{' '}
                    <strong data-id="element-1091">{formData.email}</strong>
                  </p>
                  <div className="flex items-center justify-center gap-2 text-sm text-slate-500" data-id="element-1092">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-primary" data-id="element-1093"></div>
                    Redirecting to your dashboard...
                  </div>
                </CardContent>
              </Card>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}